/**
 * @fileoverview Cell components of the Task Tracker matrix. STATUS_ICON / STATUS_COLOR are exported so the
 * legend (AR-912) stays in sync with the cells.
 */

import { memo } from 'react'
import type { ReactNode } from 'react'
import { ActionIcon, Box, Text, Tooltip } from '@mantine/core'
import { IconCheck, IconClock, IconLoader2 } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import type { TrackStatus } from '../types'
import { TeacherUploadMark } from './TeacherUploadMark'

const EMPTY_DASH = <Text size="xs" color="dimmed">—</Text>

/** Figma: 36px cell box with an 18px glyph drawn at a 2.5 stroke. */
const STATUS_ICON_SIZE = 18
const STATUS_ICON_STROKE = 2.5
export const STATUS_CELL_SIZE = 'lg'

export const STATUS_COLOR: Record<TrackStatus, string> = {
  completed: 'green',
  in_progress: 'blue',
  pending: 'orange',
}

export const STATUS_ICON: Record<TrackStatus, JSX.Element> = {
  completed: <IconCheck size={STATUS_ICON_SIZE} stroke={STATUS_ICON_STROKE} />,
  // Figma: an open arc (not the dotted IconProgress) for "in progress".
  in_progress: <IconLoader2 size={STATUS_ICON_SIZE} stroke={STATUS_ICON_STROKE} />,
  pending: <IconClock size={STATUS_ICON_SIZE} stroke={STATUS_ICON_STROKE} />,
}

interface AssessmentCellProps {
  status: TrackStatus
  enabled: boolean
  /** Lesson date in `YYYY-MM-DD`. When set, cells in the future are disabled. */
  lessonDate?: string
  tooltipCompleted: ReactNode
  tooltipInProgress: ReactNode
  tooltipPending: ReactNode
  onClick?: () => void
}

export const AssessmentCell = memo(function AssessmentCell({
  status,
  enabled,
  lessonDate,
  tooltipCompleted,
  tooltipInProgress,
  tooltipPending,
  onClick,
}: AssessmentCellProps) {
  const { t } = useTranslation()

  if (!enabled) return EMPTY_DASH

  const today = new Date().toISOString().slice(0, 10)
  const isFutureLesson = status !== 'completed' && lessonDate !== undefined && lessonDate > today

  const statusTooltips: Record<TrackStatus, ReactNode> = {
    completed: tooltipCompleted,
    in_progress: tooltipInProgress,
    pending: tooltipPending,
  }
  const tooltip = isFutureLesson ? t('task_tracker.status.future_lesson') : statusTooltips[status]

  return (
    <Tooltip label={tooltip} withArrow>
      <Box component="span" style={{ display: 'inline-block' }}>
        <ActionIcon
          variant="light"
          color={STATUS_COLOR[status]}
          size={STATUS_CELL_SIZE}
          onClick={isFutureLesson ? undefined : onClick}
          style={isFutureLesson ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
        >
          {STATUS_ICON[status]}
        </ActionIcon>
      </Box>
    </Tooltip>
  )
})

interface DeliverableCellProps {
  completed: boolean
  enabled: boolean
  uploadedByTeacher?: boolean
  tooltip: ReactNode
  onClick?: () => void
}

/** Figma: tooltip is a 240px dark card; wider labels wrap instead of stretching. */
const DELIVERABLE_TOOLTIP_WIDTH = 240

export const DeliverableCell = memo(function DeliverableCell({
  completed,
  enabled,
  uploadedByTeacher = false,
  tooltip,
  onClick,
}: DeliverableCellProps) {
  if (!enabled) return EMPTY_DASH

  const status: TrackStatus = completed ? 'completed' : 'pending'

  return (
    <Tooltip
      label={tooltip}
      withArrow
      multiline
      width={DELIVERABLE_TOOLTIP_WIDTH}
      styles={{ tooltip: { textAlign: 'left' } }}
    >
      <Box component="span" style={{ display: 'inline-block', position: 'relative' }}>
        <ActionIcon variant="light" color={STATUS_COLOR[status]} size={STATUS_CELL_SIZE} onClick={onClick}>
          {STATUS_ICON[status]}
        </ActionIcon>
        {completed && uploadedByTeacher && <TeacherUploadMark />}
      </Box>
    </Tooltip>
  )
})
